import { api } from "@/modules/api";
import { AppStore } from "@/store";
import { waitForLiveQuerySync } from "@/store/sync/utils";
import { runInAction } from "mobx";
import { logger } from "@/modules/logger";
import { toastModule } from "@/modules/toast";
import { objectModule } from "@/modules/object";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { VoiceSessionRefineSuggestionData, VoiceSessionSuggestionData } from "@/store/note/types";
import { truncateSuggestionDataForTracking } from "@/store/note/helpers/suggestionTracking";
import { Duration } from "luxon";

export const refineNoteWithVoice = async ({
  noteId,
  userPrompt,
  sessionId,
  capturedAudioId,
  generatedNoteContent,
  suggestionData,
  store,
}: {
  noteId: string;
  userPrompt: string;
  sessionId: string;
  capturedAudioId: string;
  generatedNoteContent: string;
  suggestionData: VoiceSessionSuggestionData | VoiceSessionRefineSuggestionData;
  store: AppStore;
}) => {
  const startTime = Date.now();

  try {
    const note = await store.notes.getAsync(noteId);
    if (!note) return { success: false, error: "Note not found" };
    runInAction(() => (note.isProcessingSuggestion = true));

    try {
      const response = await api.post("/v2/voice/refine_note", {
        body: {
          note_id: noteId,
          user_prompt: userPrompt,
          session_id: sessionId,
          captured_audio_id: capturedAudioId,
          generated_note_content: generatedNoteContent,
        },
      });

      if (response.response.status === 201) {
        await waitForLiveQuerySync({
          syncStore: store.sync,
          query: () => !!store.spaceAccountNotes.localTable.get(note.spaceAccountNoteId),
          timeout: Duration.fromObject({ seconds: 90 }),
        });

        // Track successful refine event
        const endTime = Date.now();
        const elapsedTime = Math.floor((endTime - startTime) / 1000);

        const { previousMarkdownContents, suggestedMarkdownContents, transcriptText, userPrompt } =
          truncateSuggestionDataForTracking(suggestionData);

        trackEvent(TrackedEvent.VoiceSessionSuggestedEditsRefine, {
          session_id: sessionId,
          captured_audio_id: capturedAudioId,
          note_id: noteId,
          elapsed_time: elapsedTime,
          previous_title: suggestionData.prev_primary_label,
          previous_markdown_contents: previousMarkdownContents,
          transcript: transcriptText,
          suggested_markdown_contents: suggestedMarkdownContents,
          suggested_title: suggestionData.suggested_primary_label,
          user_prompt: userPrompt,
        });

        return { success: true };
      } else {
        throw new Error(response.error?.detail);
      }
    } finally {
      runInAction(() => (note.isProcessingSuggestion = false));
    }
  } catch (e: unknown) {
    logger.error({
      message: "Failed to refine note",
      info: {
        error: objectModule.safeErrorAsJson(e as Error),
        noteId,
        userPrompt,
        sessionId,
        capturedAudioId,
        generatedNoteContent,
        suggestionKind: "VOICE_SESSION_V1",
      },
    });
    toastModule.triggerToast({ content: "Failed to refine note. Please try again." });
    return { success: false, error: e instanceof Error ? e.message : "Unknown error" };
  }
};
